/**
 * Travel Tips Section Component
 * Displays travel tips and articles with categories and reading time
 */

import { TravelTip, TravelTipsSection } from '../types/informational';
import { InformationalDataService } from '../services/InformationalDataService';

export class TravelTipsSectionComponent implements TravelTipsSection {
  public title: string;
  public subtitle: string;
  public tips: TravelTip[];
  public maxTips: number;
  public showCategories: boolean;
  
  private container: HTMLElement | null = null;
  private dataService: InformationalDataService;

  constructor(
    title: string = 'Travel Tips',
    subtitle: string = 'Our experts share their best tips for smart travel',
    maxTips: number = 4,
    showCategories: boolean = true
  ) {
    this.title = title;
    this.subtitle = subtitle;
    this.maxTips = maxTips;
    this.showCategories = showCategories;
    this.dataService = InformationalDataService.getInstance();
    this.tips = this.dataService.getTravelTips().slice(0, maxTips);
  }

  /**
   * Initialize the component
   */
  public init(): void {
    this.createContainer();
    this.render();
    this.bindEvents();
  }

  /**
   * Create the container element
   */
  private createContainer(): void {
    // Find insertion point in dynamic sections container
    const dynamicContainer = document.getElementById('dynamic-sections-container');
    if (!dynamicContainer) {
      console.error('TravelTips: Dynamic sections container not found');
      return;
    }

    // Create container
    this.container = document.createElement('section');
    this.container.className = 'travel-tips-section';
    this.container.id = 'travel-tips-section';
    this.container.setAttribute('aria-labelledby', 'travel-tips-title');

    // Append to dynamic container
    dynamicContainer.appendChild(this.container);
  }

  /**
   * Render the component
   */
  private render(): void {
    if (!this.container) return;

    this.container.innerHTML = `
      <div class="travel-tips-section__container">
        <header class="travel-tips-section__header">
          <h2 class="travel-tips-section__title" id="travel-tips-title">
            ${this.escapeHtml(this.title)}
          </h2>
          <p class="travel-tips-section__subtitle">
            ${this.escapeHtml(this.subtitle)}
          </p>
        </header>

        ${this.showCategories ? this.renderCategoryFilter() : ''}

        <div class="travel-tips-section__grid" role="region" aria-label="Travel articles and tips">
          ${this.tips.map(tip => this.renderTipCard(tip)).join('')}
        </div>

        <div class="travel-tips-section__actions">
          <button 
            class="btn btn-outline travel-tips-section__view-all"
            type="button"
            aria-label="View all travel tips"
          >
            View all tips
          </button>
        </div>
      </div>
    `;
  }

  /**
   * Render category filter
   */
  private renderCategoryFilter(): string {
    const categories = [
      { key: 'all', label: 'All', icon: '📚' },
      { key: 'planning', label: 'Planning', icon: '📅' },
      { key: 'packing', label: 'Packing', icon: '🎒' },
      { key: 'safety', label: 'Safety', icon: '🛡️' },
      { key: 'budget', label: 'Budget', icon: '💰' },
      { key: 'culture', label: 'Culture', icon: '🏛️' },
      { key: 'transport', label: 'Transport', icon: '🚄' }
    ];

    return `
      <div class="travel-tips-section__categories">
        <div class="tip-categories" role="tablist" aria-label="Tip categories">
          ${categories.map(category => `
            <button 
              class="tip-category ${category.key === 'all' ? 'tip-category--active' : ''}"
              type="button"
              role="tab"
              data-category="${category.key}"
              aria-selected="${category.key === 'all'}"
              aria-controls="travel-tips-grid"
            >
              <span class="tip-category__icon">${category.icon}</span>
              <span class="tip-category__label">${category.label}</span>
            </button>
          `).join('')}
        </div>
      </div>
    `;
  }

  /**
   * Render a single tip card
   */
  private renderTipCard(tip: TravelTip): string {
    const categoryLabels: { [key: string]: string } = {
      planning: 'Planning',
      packing: 'Packing',
      safety: 'Safety',
      budget: 'Budget',
      culture: 'Culture',
      transport: 'Transport'
    };

    const categoryIcons: { [key: string]: string } = {
      planning: '📅',
      packing: '🎒',
      safety: '🛡️',
      budget: '💰',
      culture: '🏛️',
      transport: '🚄'
    };

    // Generate fallback image name based on tip title
    const fallbackImageName = tip.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

    return `
      <article class="tip-card" data-tip-id="${tip.id}" data-category="${tip.category}">
        <div class="tip-card__image">
          <img 
            src="${tip.image}" 
            alt=""
            class="tip-card__img"
            loading="lazy"
            onerror="this.src='/assets/images/tips/${fallbackImageName}.jpg'; if(this.onerror) { this.src='/assets/images/tips/placeholder.jpg'; this.onerror=null; }"
          />
          <div class="tip-card__category">
            <span class="tip-card__category-icon">${categoryIcons[tip.category] || '📚'}</span>
            <span class="tip-card__category-label">${categoryLabels[tip.category] || tip.category}</span>
          </div>
        </div>
        
        <div class="tip-card__content">
          <div class="tip-card__meta">
            <div class="tip-card__author">
              <span class="tip-card__author-name">${this.escapeHtml(tip.author.name)}</span>
            </div>
            <div class="tip-card__reading-time">
              <span class="tip-card__reading-time-icon">⏱️</span>
              <span class="tip-card__reading-time-text">${tip.readTime} min</span>
            </div>
          </div>

          <h3 class="tip-card__title">
            ${this.escapeHtml(tip.title)}
          </h3>
          
          <p class="tip-card__excerpt">
            ${this.escapeHtml(tip.excerpt)}
          </p>
          
          <div class="tip-card__tags">
            ${tip.tags.slice(0, 3).map(tag => 
              `<span class="tip-card__tag">#${this.escapeHtml(tag)}</span>`
            ).join('')}
          </div>
          
          <div class="tip-card__footer">
            <div class="tip-card__publish-date">
              Publish the ${this.formatDate(tip.publishDate)}
            </div>
            <button 
              class="tip-card__read-more"
              type="button"
              data-tip-id="${tip.id}"
              aria-label="Lire l'article ${this.escapeHtml(tip.title)}"
            >
              Read Article
            </button>
          </div>
        </div>
      </article>
    `;
  }

  /**
   * Bind event listeners
   */
  private bindEvents(): void {
    if (!this.container) return;

    // Handle category filter clicks
    this.container.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      const categoryButton = target.closest('.tip-category') as HTMLElement;
      const readMoreButton = target.closest('.tip-card__read-more') as HTMLElement;
      const viewAllButton = target.closest('.travel-tips-section__view-all') as HTMLElement;

      if (categoryButton) {
        const category = categoryButton.getAttribute('data-category');
        if (category) {
          this.handleCategoryFilter(category);
        }
      } else if (readMoreButton) {
        const tipId = readMoreButton.getAttribute('data-tip-id');
        if (tipId) {
          this.handleReadMore(tipId);
        }
      } else if (viewAllButton) {
        this.handleViewAllTips();
      }
    });

    // Handle keyboard navigation
    this.container.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        const target = event.target as HTMLElement;
        if (target.classList.contains('tip-category') || 
            target.classList.contains('tip-card__read-more') ||
            target.classList.contains('travel-tips-section__view-all')) {
          event.preventDefault();
          target.click();
        }
      }
    });
  }

  /**
   * Handle category filtering
   */
  private handleCategoryFilter(category: string): void {
    // Update active category button
    const categoryButtons = this.container?.querySelectorAll('.tip-category');
    categoryButtons?.forEach(button => {
      const isActive = button.getAttribute('data-category') === category;
      button.classList.toggle('tip-category--active', isActive);
      button.setAttribute('aria-selected', isActive.toString());
    });

    // Filter tip cards
    const tipCards = this.container?.querySelectorAll('.tip-card');
    tipCards?.forEach(card => {
      const cardCategory = card.getAttribute('data-category');
      const shouldShow = category === 'all' || cardCategory === category;
      
      if (shouldShow) {
        card.classList.remove('tip-card--hidden');
        card.setAttribute('aria-hidden', 'false');
      } else {
        card.classList.add('tip-card--hidden');
        card.setAttribute('aria-hidden', 'true');
      }
    });

    console.log(`Filtered tips by category: ${category}`);
  }

  /**
   * Handle read more click
   */
  private handleReadMore(tipId: string): void {
    const tip = this.tips.find(t => t.id === tipId);
    if (!tip) return;

    console.log(`Reading article: ${tip.title}`);
    // In a real application, this would navigate to the full article
    // window.location.href = `/tips/${tipId}`;
  }

  /**
   * Handle view all tips
   */
  private handleViewAllTips(): void {
    console.log('View all tips clicked');
    // In a real application, this would navigate to the tips page
  }

  /**
   * Format date for display
   */
  private formatDate(date: Date): string {
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  }

  /**
   * Update tips data
   */
  public updateTips(tips: TravelTip[]): void {
    this.tips = tips.slice(0, this.maxTips);
    this.render();
  }

  /**
   * Escape HTML to prevent XSS
   */
  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Destroy the component
   */
  public destroy(): void {
    if (this.container) {
      this.container.remove();
      this.container = null;
    }
  }
}